import * as React from 'react';
import * as $ from 'jquery';
import S from 's-js';
import * as _ from 'underscore';
import { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { Typeahead } from './model';

export interface TypeaheadInputParams {
    model : Typeahead<any>;
    className? : string;
    placeholder? : string;
    disabled? : boolean;
    tabIndex? : number;
    specificInput? : string;
    inputRef? : (el : HTMLInputElement | null) => void;
}

export class TypeaheadInput extends SuperCriticalComponent<TypeaheadInputParams> {
    inputRef : (el : HTMLInputElement | null) => void

    constructor(props : TypeaheadInputParams) {
        super(props);
        this.inputRef = SuperCriticalReference(this, (props, el) => {
            initTypeahead(props, el);
            // If calling code supplies an inputRef, call it. But run in its own computation
            // so that whatever calling code does is decoupled from re-initing the typeahead.
            S(() => props.inputRef?.(el));
        });
    }

    srender({model, className, disabled, tabIndex, placeholder, specificInput} : TypeaheadInputParams) {
        return (
            // FIXME: This is inherently bug-prone b/c we're initializing our Twitter Typeahead on the top-level
            // element we return from our render method, but Twitter Typeahead then wraps our <input> in extra
            // DOM layers that don't correspond to any node in React's virtual DOM. This is precisely the same
            // bug that we had in our kendo date/time pickers. See comment in ../datetimepicker/view.tsx. As in
            // that case, our behavior is fine for most cases but can lead to a thrown error like
            //
            //   NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child
            //   of this node
            //
            // if we transition our view state in such a way that React tries to unmount the <TypeaheadInput />
            // component from its parent node, as React issues a removeChild call for a "child" node that's no
            // longer a true child of the parent node (it's a nested descendant instead).
            //
            // The REAL fix, as with our date/time pickers, is to add a wrapper element here, so that our
            // returned DOM looks something like <span><input /></span> instead of JUST our typeahead <input />.
            // And if we go that far, we could even move our className from the <input /> to the wrapper <span />,
            // which might make it easier to target typeahead inputs via LESS. But not doing any of that for now
            // b/c of the regression risk of changing up DOM that's used in several places.
            <input
                id={specificInput}
                className={className}
                placeholder={placeholder}
                disabled={disabled}
                tabIndex={tabIndex}
                value={model.data()}
                onChange={e => model.data(e.currentTarget.value)}
                ref={this.inputRef}
            />
        );
    }
}

function initTypeahead<T>({ model } : TypeaheadInputParams, el : HTMLInputElement) {
    let $el = $(el) as JQuery & { typeahead : any };

    $el.typeahead(model.typeaheadOptions, {
        source: model.requestQueue ? serverSideSource : clientSideSource,
        displayKey: model.displayKey,
        templates: model.templates
    })
    .on('typeahead:opened', loadDefault)
    .on('typeahead:selected', update);

    // DC 07/14/21 -- Auto-focuses the vessel input field only on Add PortCall
    $("#addPortCall").focus();

    S.cleanup(() => {
        $el.off('typeahead:opened', loadDefault);
        $el.off('typeahead:selected', update);
        $el.typeahead('destroy');
    });

    S(() => {
        // Notify the typeahead lib of any value changes that might be applied by external code. Otherwise the
        // typeahead can be left in an inconsistent state where one value is shown in the input element, but the
        // dropdown menu acts as if a different value is selected.
        if (model.data() !== $el.typeahead('val')) {
            $el.typeahead('val', model.data());
        }
    });

    function update(evt : Event, item : any) {
        model.data(model.displayKey(item));
        if (model.onSelect) model.onSelect(item);
    }

    function loadDefault() {
        // If the dropdown is empty when the typeahead is opened, simulate the user pressing the down arrow to
        // trigger the typeahead to load search results for the empty string, if any.
        // CAUTION: This is digging into typeahead internals (the .data('ttTypeahead') bit) to detect whether
        // the dropdown is empty or not. I haven't found another way to detect this yet. MAKE SURE TO RE_TEST if
        // we ever upgrade our copy of the typeahead lib. The check to ensure the dropdown is empty is needed to
        // prevent the following undesirable UX:
        // 1. The user selects an item from the typeahead dropdown.
        // 2. That triggers some upstream code to clear the typeahead's value by setting model.data() to the
        //    empty string. E.g. the recipient selector for new PC log comments does this. We pick that up
        //    above and effectively call $el.typeahead('val', '');
        // 3. The typehead input remains focused through steps 1 and 2. The user starts typing a new search
        //    query into the typeahead input WITHOUT BLURRING THE INPUT FIRST. E.g. the user starts typing the
        //    name of the next intended recipient for a PC log comment.
        // 4. The user's first keystroke causes the typeahead to fire its 'typeahead:opened' event, for which
        //    this loadDefault function is a handler. If we were to fire a synthetic down arrow press at this
        //    point, the typeahead would think the user was trying to highlight the next item from the
        //    still-populated dropdownt list, causing the typeahead to overwrite the input's value with the text
        //    of that next item. That's annoying b/c it clobbers the user's first keystroke and means the user
        //    has to clear the input and start typing AGAIN before the typeahead will honor their typing.
        // It turns out that how the typeahead lib interprets down arrow presses depends on the state of the
        // dropdown list at the time -- If the dropdown list's isEmpty flag is set, then the typeahead lib
        // interprets a down arrow press as "update the dropdown list w/o trying to update the input's value,"
        // which is exactly the behavior we want here. If the dropdown list's isEmpty flag is NOT set and the
        // entered search query matches the typeahead's minLength option (for which we often pass 0), then the
        // typeahead treats a down arrow press as "highlight the next dropdown list item and update the input's
        // value," which is the behavior we wanna avoid.
        if ($el.data('ttTypeahead').dropdown.isEmpty) {
            const ev = $.Event("keydown");
            // keyCode 40 == down arrow
            ev.keyCode = ev.which = 40;
            $el.trigger(ev);
            // after typeahead opens, select all text in input
            setTimeout(() => $el.select(), 60);
            return true;
        }
    }

    function clientSideSource(query : string, cb : (items : T[])=> void) {
        let col = model.collection!(),
            matches = col.filter(item => !query || model.matchFn!(item, query));
        cb(matches);
    }

    function serverSideSource(query : string, cb : (items : T[])=> void) {
        // TODO: Cache results?
        model.requestQueue!.add(query, function (data : T[]) {
            cb(data);
            model.onSearchResults && model.onSearchResults(data);
        });
    }
}


// WEBPACK FOOTER //
// ./apps/portcall/typeahead/view.tsx